import type { IConditionBaseDropDownProps, IDynamicFilterRule, IItem } from "@similarweb/pro-logic-components";
import {
    DROPDOWN_MATCH_TYPE_OPTIONS,
    DROPDOWN_TYPE_OPTIONS,
    multiIncludeExcludeType,
    TYPE_KEYWORD,
    TYPE_KEYWORD_LIST,
    translationKeysMap,
} from "./consts";
import { translate } from "app/filters/i18nFilter";
import type { IconName } from "@similarweb/icons";
import type { DynamicFilterRuleForUrl, IParsedKeyword } from "./types";
import { keywordGroupsQueryClient } from "app/services/keyword-groups/utils/keywordGroupsQueryClient";

const DEFAULT_FILTER_TYPE = "keyword";

export const getDropdownOptions = (
    filterType: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType] = multiIncludeExcludeType.KEYWORD,
): IConditionBaseDropDownProps[] =>
    [DROPDOWN_TYPE_OPTIONS, DROPDOWN_MATCH_TYPE_OPTIONS[filterType]].map((option) => {
        return {
            ...option,
            options: option.options.map((opt) => {
                return {
                    ...opt,
                    title: translate(opt.title),
                    ...(opt.description ? { description: translate(opt.description) } : {}),
                };
            }),
        };
    });

export const getFilterButtonText = (
    rules: IDynamicFilterRule[],
    filterType: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType],
) => {
    const selectedKeys = translationKeysMap[filterType];
    return rules?.length === 1
        ? translate(selectedKeys.chipBtnTextSingleKey)
        : translate(selectedKeys.chipBtnTextKey, {
              count: rules.length,
          });
};
export const getRules = async (
    filterType: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType],
    multiIncludeExcludeKeywords: string,
    multiIncludeExcludeUrl: string,
    multiIncludeExcludeTitle: string,
    multiIncludeExcludeTopic: string,
    emptyRule: IDynamicFilterRule,
): Promise<IDynamicFilterRule[]> => {
    const filterMap = {
        keyword: multiIncludeExcludeKeywords,
        url: multiIncludeExcludeUrl,
        title: multiIncludeExcludeTitle,
        topic: multiIncludeExcludeTopic,
    };

    const filter = filterMap[filterType];
    return filter && JSON.parse(filter)?.length ? await adaptRulesFromUrl(JSON.parse(filter)) : [emptyRule];
};

export const adaptRulesFromUrl = async (
    rules: (DynamicFilterRuleForUrl & { field: string })[],
): Promise<IDynamicFilterRule[]> =>
    Promise.all(
        rules.map(async (rule) => {
            const adaptedRule = {
                ...rule,
                match: rule.matchType,
                items: await Promise.all(
                    rule.items.map(async (item) => {
                        const isKeywordGroup = item.type === TYPE_KEYWORD_LIST;
                        let text = item?.value;

                        if (isKeywordGroup) {
                            try {
                                const group = await keywordGroupsQueryClient.getGroupById(item?.value);
                                text = group?.Name || item?.value;
                            } catch (error) {
                                text = item?.value;
                            }
                        }

                        return {
                            type: item.type,
                            text,
                            ...(isKeywordGroup && { id: item.value }),
                            iconName: (isKeywordGroup ? "keyword-group" : "keywords") as IconName,
                        };
                    }),
                ),
            };
            const { field, matchType, ...cleanedRule } = adaptedRule;
            return cleanedRule;
        }),
    );
export const adaptRulesForUrl = (
    rules: IDynamicFilterRule[],
    filterType?: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType],
): DynamicFilterRuleForUrl[] =>
    rules?.map((rule) => {
        const adaptedRule = {
            ...rule,
            field: filterType ?? DEFAULT_FILTER_TYPE,
            matchType: rule.match,
            items: rule.items.map((item) => {
                return {
                    type: item.type === TYPE_KEYWORD_LIST ? TYPE_KEYWORD_LIST : filterType,
                    value: item.id ?? item.text,
                };
            }),
        };
        delete adaptedRule.match;
        return adaptedRule;
    }) as DynamicFilterRuleForUrl[];

export const getFilterList = (type: "keyword" | "keywordList", items: IItem[]) =>
    items.filter((item) => item.type === type).map(({ id }) => id);

export const getParsedKeywords = (
    value: string,
    filterType?: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType],
): IParsedKeyword[] =>
    value
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item)
        .map((item) => ({
            type: filterType ?? TYPE_KEYWORD,
            text: item,
            iconName: "keywords" as IconName,
        }));
